import clsx from 'clsx';
import ImageWithFallback from 'components/ImageWithFallback';
import { cloneElement, ReactElement, ReactNode } from 'react';
import ChevronLeft15 from '../../svg/ChevronLeft15.svg';
import styles from './imageWithText.module.css';

type CommonProps = {
  /** A wrapper around the image, otherwise a <div/> will be used as the fallback. The wrapper
   * should be provided as JSX markup, e.g. overrideImageWrapper={<button onClick={()=>{}} />}
   */
  overrideImageWrapper?: ReactElement<{
    className?: string;
    children?: ReactNode;
  }>;
  imageSrc: string;
  imageAlt?: string;
  imageOverlayChildren?: ReactNode;
  desktopSize?: 'full' | 'half' | 'third';
  cta?: 'chevron' | undefined;
  imageSize?: undefined | 'small';
};

type Props =
  // Attempt to enforce only allow either title or textChildren
  | (CommonProps & {
      title?: 'ERROR: Providing both title and textChildren is not supported, as there will be no gap between them, instead textChildren should include the title too' & {
        error: true;
      };
      textChildren?: ReactNode;
    })
  | (CommonProps & {
      title?: string;
      textChildren?: 'ERROR: Providing both title and textChildren is not supported, as there will be no gap between them, instead textChildren should include the title too' & {
        error: true;
      };
    });

const ImageWithText = ({
  imageSrc,
  imageAlt,
  imageOverlayChildren,
  overrideImageWrapper,
  desktopSize,
  title,
  textChildren,
  cta,
  imageSize,
}: Props) => {
  const wrapperClassName = clsx(
    styles.image,
    imageSize === 'small' && styles.imageSmall,
  );

  const imageContent = (
    <>
      <ImageWithFallback
        src={imageSrc}
        layout="fill"
        className={styles.thumbnailImage}
        alt={imageAlt ? imageAlt : 'Image'}
      />
      {imageOverlayChildren && (
        <div className={styles.imageOverlay}>{imageOverlayChildren}</div>
      )}
    </>
  );

  const imageWithWrapper = overrideImageWrapper ? (
    cloneElement(overrideImageWrapper, {
      className: clsx(wrapperClassName, overrideImageWrapper.props.className),
      children: imageContent,
    })
  ) : (
    <div className={wrapperClassName}>{imageContent}</div>
  );

  return (
    <div
      className={
        styles.imageWithText + ` ${styles['imageWithText__' + desktopSize]}`
      }
    >
      {imageWithWrapper}
      <div className={styles.textSection}>
        {title && <div className={styles.title}>{title}</div>}
        {textChildren && textChildren}
      </div>
      {cta === 'chevron' && <ChevronLeft15 className={styles.chevron} />}
    </div>
  );
};

export default ImageWithText;
